import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/context/LanguageContext";
import { getCheckoutStatus } from "@/lib/api";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 15;

export default function OrderSuccess() {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const L = {
    en: {
      polling: "VERIFYING PAYMENT…",
      pollingSub: "Talking to Stripe. This usually takes 2–5 seconds.",
      paid: "PAYMENT CONFIRMED",
      paidSub: "Your order is locked in. Confirmation email is on its way.",
      failed: "WE COULDN'T VERIFY YOUR PAYMENT",
      failedSub: "No charge may have been made. Check your email or contact us.",
      expired: "SESSION EXPIRED",
      expiredSub: "Your checkout session timed out. No payment was made.",
      order: "ORDER #",
      amount: "AMOUNT",
      retry: "TRY AGAIN →",
      back: "← back to product",
      support: "Contact support: inforuncrvd@gmail.com",
      next: "WHAT HAPPENS NEXT",
      steps: [
        "We start cutting your lip within 24h on business days.",
        "CNC + epoxy sealing + UV varnish: 3–5 days.",
        "Tracked shipping: 5–7 days EU, 10–14 worldwide.",
        "You'll get a tracking number by email when the parcel leaves Milan.",
      ],
    },
    it: {
      polling: "VERIFICA PAGAMENTO…",
      pollingSub: "Stiamo parlando con Stripe. Di solito servono 2–5 secondi.",
      paid: "PAGAMENTO CONFERMATO",
      paidSub: "Il tuo ordine è bloccato. Ti arriverà la conferma via email.",
      failed: "NON SIAMO RIUSCITI A VERIFICARE IL PAGAMENTO",
      failedSub: "Potrebbe non essere stato addebitato nulla. Controlla la mail o contattaci.",
      expired: "SESSIONE SCADUTA",
      expiredSub: "La sessione di checkout è scaduta. Nessun addebito effettuato.",
      order: "ORDINE N°",
      amount: "IMPORTO",
      retry: "RIPROVA →",
      back: "← torna al prodotto",
      support: "Supporto: inforuncrvd@gmail.com",
      next: "COSA SUCCEDE ORA",
      steps: [
        "Entro 24h lavorative iniziamo a tagliare il tuo labbro.",
        "CNC + sigillo epossidico + vernice UV: 3–5 giorni.",
        "Spedizione tracciata: 5–7 giorni UE, 10–14 nel resto del mondo.",
        "Riceverai il tracking via email quando il pacco lascia Milano.",
      ],
    },
  }[lang];

  const title = L[state] || L.polling;
  const sub = L[`${state}Sub`] || L.pollingSub;
  const isSuccess = state === "paid";

  return (
    <div className="min-h-screen bg-ink text-bone flex flex-col">
      <NavBar onBuy={() => navigate("/product")} />

      <main className="flex-1 pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          {/* Status card */}
          <div
            data-testid="order-success-card"
            className="border border-[#333] bg-steel/30 p-8 md:p-12 relative corner-ticks"
          >
            <div className="flex items-center gap-3 mb-6">
              {state === "polling" && (
                <div className="w-3 h-3 border-2 border-wood border-t-transparent rounded-full animate-spin" />
              )}
              {isSuccess && <div className="w-3 h-3 bg-wood" />}
              {(state === "failed" || state === "expired") && (
                <div className="w-3 h-3 bg-danger animate-pulse" />
              )}
              <span
                className={`font-mono text-[11px] tracking-tech ${
                  isSuccess
                    ? "text-wood"
                    : state === "polling"
                    ? "text-bone/60"
                    : "text-danger"
                }`}
              >
                {state === "polling"
                  ? "// LIVE"
                  : isSuccess
                  ? "// PAID"
                  : state === "expired"
                  ? "// EXPIRED"
                  : "// ERROR"}
              </span>
            </div>

            <h1
              className="font-display tracking-brutal text-4xl md:text-6xl uppercase leading-[0.95] text-bone"
              data-testid="order-success-title"
            >
              {title}
            </h1>
            <p className="mt-4 font-mono text-sm text-bone/60 max-w-xl">{sub}</p>

            {info?.order_number && (
              <div className="mt-8 grid grid-cols-2 border-t border-b border-[#222]">
                <div className="py-5 px-1">
                  <div className="font-mono text-[9px] tracking-tech text-bone/40">{L.order}</div>
                  <div className="font-display text-2xl text-wood mt-1" data-testid="order-success-number">
                    {info.order_number}
                  </div>
                </div>
                {typeof info.amount_total === "number" && (
                  <div className="py-5 px-1 border-l border-[#222]">
                    <div className="font-mono text-[9px] tracking-tech text-bone/40">
                      {L.amount}
                    </div>
                    <div className="font-display text-2xl text-wood mt-1">
                      €{(info.amount_total / 100).toFixed(0)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {isSuccess && (
              <div className="mt-10">
                <div className="font-mono text-[10px] tracking-tech text-wood mb-4">
                  {L.next}
                </div>
                <ul className="space-y-3">
                  {L.steps.map((s, i) => (
                    <li key={i} className="flex gap-4 font-mono text-sm text-bone/80">
                      <span className="text-wood">{String(i + 1).padStart(2, "0")}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              {!isSuccess && (
                <Link
                  to="/product"
                  data-testid="order-success-retry"
                  className="inline-block bg-wood text-ink font-mono text-xs tracking-tech px-6 py-3 hover:bg-bone transition-colors"
                >
                  {L.retry}
                </Link>
              )}
              <Link
                to="/"
                className="inline-block border border-[#333] text-bone font-mono text-xs tracking-tech px-6 py-3 hover:border-wood hover:text-wood transition-colors"
              >
                {L.back}
              </Link>
              <a
                href="mailto:inforuncrvd@gmail.com"
                className="font-mono text-xs tracking-tech text-bone/50 hover:text-wood py-3"
              >
                {L.support}
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
